import React, { useState } from "react";
import { login, hasPermission } from "./Auth";
import AdminDashboard from "./AdminDashboard";
import TeacherPlatform from "./TeacherPlatform";

// ─────────────────────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────────────────────
const C = {
  navy:"#0A1628", blue:"#1560BD", sky:"#2E8BC0", teal:"#00A878",
  white:"#FFFFFF", mist:"#E2EAF4", slate:"#5A7293", bg:"#EEF3FA",
  error:"#E03B3B", rose:"#FFF0F0", amber:"#F59E0B", purple:"#7C3AED",
};

// ─────────────────────────────────────────────────────────────
// LOGIN SCREEN
// ─────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);

  const handleSubmit = () => {
    if (!email || !password) { setError("Please enter your email and password."); return; }
    setLoading(true); setError("");
    setTimeout(() => {
      const result = login(email, password);
      if (result.success) { onLogin(result.user); }
      else { setError(result.error); setLoading(false); }
    }, 600);
  };

  const DEMOS = [
    { label:"Dr. Patricia Moore — Principal",        email:"pmoore@school.edu",    pw:"admin123",   badge:"ADMIN",   color:C.blue },
    { label:"James Carter — Asst. Principal",        email:"jcarter@school.edu",   pw:"admin123",   badge:"ADMIN",   color:C.blue },
    { label:"Sandra Lee — Data Coordinator",         email:"slee@school.edu",      pw:"admin123",   badge:"ADMIN",   color:C.purple },
    { label:"Ms. ER — Elba Riley (K/1st)",           email:"eriley@school.edu",    pw:"teacher123", badge:"TEACHER", color:C.teal },
    { label:"Ms. IG — Indira Gonzalez (5th)",         email:"igonzalez@school.edu",  pw:"teacher123", badge:"TEACHER", color:C.teal },
    { label:"Ms. JC — Judith Concepcion (K)",         email:"jconcepcion@school.edu",pw:"teacher123", badge:"TEACHER", color:C.teal },
    { label:"Ms. KD — Kirsa De La Cruz (1st)",        email:"kdelacruz@school.edu",  pw:"teacher123", badge:"TEACHER", color:C.teal },
    { label:"Ms. LO — Lores Olea (5th)",              email:"lolea@school.edu",      pw:"teacher123", badge:"TEACHER", color:C.teal },
    { label:"Mr. DW — Daniel Wyner (2nd)",            email:"dwyner@school.edu",     pw:"teacher123", badge:"TEACHER", color:C.teal },
  ];

  return (
    <div style={{
      minHeight:"100vh",
      background:`linear-gradient(145deg, ${C.navy} 0%, #0E2240 55%, #132F52 100%)`,
      display:"flex", alignItems:"center", justifyContent:"center",
      fontFamily:"'DM Sans','Segoe UI',sans-serif", padding:20,
    }}>
      {/* Grid overlay */}
      <div style={{
        position:"fixed", inset:0, opacity:.05,
        backgroundImage:"linear-gradient(#3B9EE2 1px,transparent 1px),linear-gradient(90deg,#3B9EE2 1px,transparent 1px)",
        backgroundSize:"44px 44px", pointerEvents:"none",
      }}/>

      <div style={{ width:"100%", maxWidth:500, position:"relative", zIndex:1 }}>
        {/* Logo */}
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <div style={{
            width:64, height:64, borderRadius:18, margin:"0 auto 14px",
            background:"#0A1628",
            display:"flex", alignItems:"center", justifyContent:"center",
            boxShadow:`0 8px 28px rgba(0,0,0,.5)`,
            border:"1.5px solid #1A3A5C",
          }}>
            <svg width="46" height="46" viewBox="0 0 46 46" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Mortarboard brim - wide green bar */}
              <rect x="6" y="11" width="30" height="3.5" rx="1.5" fill="#00A878"/>
              {/* Cap body - wide flat-bottom triangle */}
              <polygon points="21,14.5 36,21 21,27.5 6,21" fill="#2E8BC0"/>
              {/* Tassel - drops DOWN from right edge */}
              <line x1="35" y1="21" x2="35" y2="30" stroke="#F59E0B" strokeWidth="2.5" strokeLinecap="round"/>
              <circle cx="35" cy="32" r="2.5" fill="#F59E0B"/>
              {/* 4 data bars at bottom */}
              <rect x="6"  y="35" width="5" height="7"  rx="1" fill="#1560BD"/>
              <rect x="13" y="32" width="5" height="10" rx="1" fill="#2E8BC0"/>
              <rect x="20" y="29" width="5" height="13" rx="1" fill="#00A878"/>
              <rect x="27" y="33" width="5" height="9"  rx="1" fill="#2E8BC0" opacity="0.7"/>
            </svg>
          </div>>>
          <div style={{ fontSize:24, fontWeight:900, color:C.white }}>
            Elite Scholastics <span style={{ color:C.sky }}>< /span>
          </div>
          <div style={{ fontSize:11, color:"#6A8FB0", marginTop:3, letterSpacing:".12em" }}>
            SCHOOL INTELLIGENCE PLATFORM
          </div>
        </div>

        {/* Card */}
        <div style={{
          background:"rgba(255,255,255,.97)", borderRadius:20,
          padding:"28px 32px", boxShadow:"0 20px 60px rgba(0,0,0,.28)",
        }}>
          <div style={{ fontSize:17, fontWeight:900, color:C.navy, marginBottom:3 }}>Sign In</div>
          <div style={{ fontSize:12, color:C.slate, marginBottom:20 }}>
            Access is role-based. Admins see everything. Teachers see only their own class.
          </div>

          {error && (
            <div style={{ background:C.rose, border:`1px solid ${C.error}44`, borderRadius:8,
              padding:"9px 14px", marginBottom:14, fontSize:12, color:C.error, fontWeight:600 }}>
              ⚠ {error}
            </div>
          )}

          <div style={{ marginBottom:12 }}>
            <label style={{ fontSize:10, fontWeight:700, color:C.slate, display:"block", marginBottom:4,
              textTransform:"uppercase", letterSpacing:".06em" }}>Email</label>
            <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&handleSubmit()}
              placeholder="yourname@school.edu"
              style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:`1.5px solid ${C.mist}`,
                fontSize:13, color:C.navy, outline:"none", boxSizing:"border-box", fontFamily:"inherit" }}
              onFocus={e=>e.target.style.borderColor=C.blue}
              onBlur={e=>e.target.style.borderColor=C.mist}/>
          </div>

          <div style={{ marginBottom:20 }}>
            <label style={{ fontSize:10, fontWeight:700, color:C.slate, display:"block", marginBottom:4,
              textTransform:"uppercase", letterSpacing:".06em" }}>Password</label>
            <input type="password" value={password} onChange={e=>setPassword(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&handleSubmit()}
              placeholder="••••••••"
              style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:`1.5px solid ${C.mist}`,
                fontSize:13, color:C.navy, outline:"none", boxSizing:"border-box", fontFamily:"inherit" }}
              onFocus={e=>e.target.style.borderColor=C.blue}
              onBlur={e=>e.target.style.borderColor=C.mist}/>
          </div>

          <button onClick={handleSubmit} disabled={loading} style={{
            width:"100%", padding:"12px", borderRadius:10, border:"none",
            background:loading?C.slate:`linear-gradient(135deg,${C.blue},${C.sky})`,
            color:C.white, fontSize:14, fontWeight:800, cursor:loading?"wait":"pointer",
            boxShadow:loading?"none":`0 4px 14px ${C.blue}44`,
            fontFamily:"inherit", transition:"all .2s",
          }}>
            {loading ? "Signing in…" : "Sign In →"}
          </button>

          {/* Demo accounts */}
          <div style={{ marginTop:22 }}>
            <div style={{ fontSize:10, fontWeight:700, color:C.slate, textAlign:"center",
              marginBottom:10, textTransform:"uppercase", letterSpacing:".07em" }}>
              — Quick Access (Demo Accounts) —
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
              {DEMOS.map(d => (
                <button key={d.email} onClick={()=>{setEmail(d.email);setPassword(d.pw);setError("");}} style={{
                  border:`1px solid ${d.color}33`, background:d.color+"07",
                  borderRadius:7, padding:"7px 12px", cursor:"pointer",
                  display:"flex", justifyContent:"space-between", alignItems:"center",
                  fontFamily:"inherit", textAlign:"left",
                }}>
                  <span style={{ fontSize:12, fontWeight:600, color:C.navy }}>{d.label}</span>
                  <span style={{ fontSize:10, fontWeight:800, background:d.color,
                    color:C.white, borderRadius:99, padding:"2px 8px" }}>{d.badge}</span>
                </button>
              ))}
            </div>
            <div style={{ fontSize:10, color:C.slate, textAlign:"center", marginTop:10 }}>
              Admin password: <code style={{ background:C.mist, padding:"1px 5px", borderRadius:3 }}>admin123</code>
              &nbsp;·&nbsp;
              Teacher password: <code style={{ background:C.mist, padding:"1px 5px", borderRadius:3 }}>teacher123</code>
            </div>
          </div>
        </div>

        <div style={{ textAlign:"center", marginTop:16, fontSize:11, color:"#3A5470" }}>
          FERPA Compliant · Role-Based Access · All student data encrypted
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ACCESS DENIED
// ─────────────────────────────────────────────────────────────
function AccessDenied({ user, onGoBack }) {
  return (
    <div style={{ minHeight:"80vh", display:"flex", alignItems:"center", justifyContent:"center",
      fontFamily:"'DM Sans','Segoe UI',sans-serif" }}>
      <div style={{ textAlign:"center", maxWidth:420 }}>
        <div style={{ fontSize:56, marginBottom:14 }}>🔒</div>
        <div style={{ fontSize:22, fontWeight:900, color:C.navy, marginBottom:8 }}>Access Restricted</div>
        <div style={{ fontSize:14, color:C.slate, lineHeight:1.8, marginBottom:22 }}>
          The Admin Dashboard is only available to administrators. As a teacher, you can only view
          <strong> your own classroom data</strong>.
        </div>
        <div style={{ background:C.bg, borderRadius:12, padding:"14px 18px",
          border:`1px solid ${C.mist}`, marginBottom:20, textAlign:"left" }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.slate, marginBottom:6, textTransform:"uppercase" }}>Your Access</div>
          <div style={{ fontSize:13, color:C.navy, fontWeight:700 }}>👤 {user.name}</div>
          <div style={{ fontSize:12, color:C.slate }}>{user.title} · Grade {user.grades} · {user.subject}</div>
          <div style={{ fontSize:12, color:C.teal, marginTop:6, fontWeight:600 }}>✓ Your students · groups · session data</div>
          <div style={{ fontSize:12, color:C.error, fontWeight:600 }}>✗ Other teachers · admin analytics · observations</div>
        </div>
        <button onClick={onGoBack} style={{
          background:C.teal, color:C.white, border:"none", borderRadius:10,
          padding:"11px 24px", cursor:"pointer", fontSize:13, fontWeight:700, fontFamily:"inherit",
        }}>Go to My Classroom →</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TOP NAV BAR
// ─────────────────────────────────────────────────────────────
function NavBar({ user, view, onSetView, onLogout }) {
  const isAdmin = user.role === "admin";
  return (
    <div style={{
      background:C.navy, height:52, display:"flex", alignItems:"center",
      justifyContent:"space-between", padding:"0 20px",
      position:"sticky", top:0, zIndex:200,
      boxShadow:"0 2px 20px rgba(0,0,0,.25)",
      fontFamily:"'DM Sans','Segoe UI',sans-serif",
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <div style={{ width:28, height:28, borderRadius:7,
          background:"#0A1628",border:"1px solid #1A3A5C",
          display:"flex", alignItems:"center", justifyContent:"center" }}>
          <svg width="20" height="20" viewBox="0 0 46 46" fill="none">
            <rect x="6" y="11" width="30" height="3.5" rx="1.5" fill="#00A878"/>
            <polygon points="21,14.5 36,21 21,27.5 6,21" fill="#2E8BC0"/>
            <line x1="35" y1="21" x2="35" y2="30" stroke="#F59E0B" strokeWidth="2.5" strokeLinecap="round"/>
            <circle cx="35" cy="32" r="2.5" fill="#F59E0B"/>
            <rect x="6"  y="35" width="5" height="7"  rx="1" fill="#1560BD"/>
            <rect x="13" y="32" width="5" height="10" rx="1" fill="#2E8BC0"/>
            <rect x="20" y="29" width="5" height="13" rx="1" fill="#00A878"/>
            <rect x="27" y="33" width="5" height="9"  rx="1" fill="#2E8BC0" opacity="0.7"/>
          </svg>
        </div>>>
        <span style={{ fontWeight:900, fontSize:14, color:C.white }}>Elite Scholastics</span>
        <span style={{ color:C.sky, fontWeight:900, fontSize:14 }}>< /span>
      </div>

      <div style={{ display:"flex", gap:4 }}>
        {isAdmin && (
          <button onClick={()=>onSetView("admin")} style={{
            border:"none", borderRadius:7, padding:"5px 14px", cursor:"pointer",
            fontSize:12, fontWeight:700, fontFamily:"inherit",
            background:view==="admin"?C.blue:"transparent",
            color:view==="admin"?C.white:"#7A93B0",
          }}>🏫 Admin Dashboard</button>
        )}
        <button onClick={()=>onSetView("teacher")} style={{
          border:"none", borderRadius:7, padding:"5px 14px", cursor:"pointer",
          fontSize:12, fontWeight:700, fontFamily:"inherit",
          background:view==="teacher"?C.teal:"transparent",
          color:view==="teacher"?C.white:"#7A93B0",
        }}>👩‍🏫 {isAdmin?"Teacher Studio":"My Classroom"}</button>
      </div>

      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <div style={{ textAlign:"right" }}>
          <div style={{ fontSize:12, fontWeight:700, color:C.white }}>{user.name}</div>
          <div style={{ fontSize:10, color:"#6A8FB0" }}>
            {user.title} · {isAdmin?"Full Access":`Gr ${user.grades} · Own data only`}
          </div>
        </div>
        <div style={{ width:30, height:30, borderRadius:8,
          background:isAdmin?C.blue:C.teal,
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:11, fontWeight:900, color:C.white }}>
          {user.avatar}
        </div>
        <button onClick={onLogout} style={{
          border:`1px solid rgba(255,255,255,.15)`, background:"transparent",
          borderRadius:6, padding:"4px 10px", cursor:"pointer",
          fontSize:11, fontWeight:600, color:"#6A8FB0", fontFamily:"inherit",
        }}>Sign Out</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ROOT APP
// ─────────────────────────────────────────────────────────────
export default function App() {
  const [user,   setUser]   = useState(null);
  const [view,   setView]   = useState(null);
  const [denied, setDenied] = useState(false);

  const handleLogin = (u) => {
    setUser(u);
    setView(u.role === "admin" ? "admin" : "teacher");
    setDenied(false);
  };

  const handleLogout = () => { setUser(null); setView(null); setDenied(false); };

  const handleSetView = (v) => {
    if (v === "admin" && user?.role !== "admin") { setDenied(true); return; }
    setDenied(false);
    setView(v);
  };

  if (!user) return <LoginScreen onLogin={handleLogin} />;

  return (
    <div style={{ background:C.bg, minHeight:"100vh", fontFamily:"'DM Sans','Segoe UI',sans-serif" }}>
      <NavBar user={user} view={view} onSetView={handleSetView} onLogout={handleLogout}/>
      {denied && <AccessDenied user={user} onGoBack={()=>{setDenied(false);setView("teacher");}}/>}
      {!denied && view==="admin"   && hasPermission(user,"canViewAdminDashboard") && <AdminDashboard currentUser={user} onViewTeacherClass={(teacherId)=>{setView("teacher");}}/>}
      {!denied && view==="teacher" && <TeacherPlatform currentUser={user}/>}
    </div>
  );
}
